import React, { useMemo, useState } from 'react'
import {
  ProgressBar,
  PROGRESS_BAR_PREVIEW_STATE_LABELS,
  PROGRESS_BAR_PREVIEW_STATES,
  PROGRESS_BAR_PREVIEW_VALUES,
  type ProgressBarPreviewState,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './ProgressBarShowcase.css'

const ProgressBarShowcase: React.FC = () => {
  const [value, setValue] = useState(45)
  const [highlightedState, setHighlightedState] =
    useState<ProgressBarPreviewState>('partial')
  const [darkMode, setDarkMode] = useState(false)

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'progress-bar-highlight-state',
            label: 'Highlighted state',
            control: (
              <select
                id="progress-bar-highlight-state"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as ProgressBarPreviewState,
                  )
                }
              >
                {PROGRESS_BAR_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {PROGRESS_BAR_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'progress-bar-value',
            label: 'Live value',
            control: (
              <input
                id="progress-bar-value"
                className="ui-field-control"
                type="range"
                min={0}
                max={100}
                step={1}
                value={value}
                onChange={(event) => setValue(Number(event.target.value))}
              />
            ),
          },
          {
            id: 'progress-bar-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="progress-bar-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [darkMode, highlightedState, value],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Progress bar"
        description="Pill-shaped linear progress with a centered percentage label."
        stageClassName="ui-preview__stage--wide progress-bar-showcase__stage-wrap"
      >
        <div
          className={`progress-bar-showcase__sheet${
            darkMode ? ' progress-bar-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Current value: {Math.round(value)}%
            </p>
            <ProgressBar value={value} darkMode={darkMode} />
          </section>

          <section className="progress-bar-showcase__matrix">
            <h4 className="progress-bar-showcase__section-title">States</h4>
            {PROGRESS_BAR_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`progress-bar-showcase__row${
                  previewState === highlightedState
                    ? ' progress-bar-showcase__row--selected'
                    : ''
                }${darkMode ? ' progress-bar-showcase__row--dark' : ''}`}
              >
                <span className="progress-bar-showcase__row-label">
                  {PROGRESS_BAR_PREVIEW_STATE_LABELS[previewState]}
                  <span className="progress-bar-showcase__row-meta">
                    {PROGRESS_BAR_PREVIEW_VALUES[previewState]}%
                  </span>
                </span>
                <ProgressBar previewState={previewState} darkMode={darkMode} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Progress bar"
        sections={propertySections}
      />
    </>
  )
}

export default ProgressBarShowcase
